<div
  class="hover:border-primary-200 rounded-lg border border-solid border-gray-300 bg-white p-4 transition-all duration-200 hover:shadow-sm"
  [ngClass]="{
    'border-success-200 bg-success-50': stepStore.isComplete(),
    'border-warn-200': stepStore.isOverdue() && !stepStore.isComplete()
  }"
>
  <div class="mb-2 flex items-center justify-between gap-4">
    <div class="flex flex-wrap items-center gap-4">
      <h3 class="!m-0">
        {{ step().title }}
      </h3>
      @if (stepStore.stepProgress()) {
        <span
          class="text-primary-700 bg-primary-100 rounded px-2 py-0.5 text-xs font-semibold"
          [matTooltip]="getStepProgressTooltip()"
        >
          {{ stepStore.getStepProgressLabel() }}
        </span>
      }
      <pr-practice-setup-step-status [dense]="true" [status]="step().status!" />
    </div>
    <div class="flex shrink-0 items-center gap-2">
      <button
        mat-stroked-button
        color="primary"
        class="shrink-0"
        (click)="navigateToStep()"
      >
        <mat-icon>arrow_forward</mat-icon>
        Go to this step
      </button>
      @if (!stepStore.isComplete()) {
        <button
          mat-stroked-button
          color="primary"
          class="disabled:opacity-50"
          [disabled]="stepStore.mutating()"
          (click)="openAssignDialog()"
        >
          <mat-icon>person_add</mat-icon>
          Assign
        </button>
        <div
          [matTooltipDisabled]="
            stepStore.mutating() ||
            ((hasUnmetDependencies() || !stepStore.canComplete()) &&
              !isManagement())
          "
          [matTooltip]="getMarkCompleteTooltip()"
        >
          @if (
            isManagement() &&
            (!stepStore.canComplete() || hasUnmetDependencies())
          ) {
            <button
              mat-stroked-button
              class="disabled:opacity-50"
              matTooltipPosition="below"
              color="warn"
              [disabled]="stepStore.mutating()"
              (click)="markComplete(true)"
            >
              @if (stepStore.mutating()) {
                <mat-icon>hourglass_empty</mat-icon>
              } @else {
                <mat-icon>check_circle</mat-icon>
              }
              Override Complete
            </button>
          } @else {
            <button
              mat-raised-button
              class="disabled:opacity-50"
              matTooltipPosition="below"
              color="primary"
              [disabled]="
                stepStore.mutating() ||
                hasUnmetDependencies() ||
                !stepStore.canComplete()
              "
              (click)="markComplete()"
            >
              @if (stepStore.mutating()) {
                <mat-icon>hourglass_empty</mat-icon>
              } @else {
                <mat-icon>check_circle</mat-icon>
              }
              Mark Complete
            </button>
          }
        </div>
      } @else if (isManagement() && stepStore.canReopen()) {
        <button
          mat-stroked-button
          color="accent"
          (click)="stepStore.reopenStep()"
        >
          <mat-icon>refresh</mat-icon>
          Reopen Step
        </button>
      }
    </div>
  </div>

  @if (wizardStore.error()) {
    <div
      class="border-warn-200 bg-warn-50 text-warn-700 mb-3 flex items-center gap-2 rounded border px-4 py-3 text-sm"
    >
      <mat-icon class="text-warn-500">error</mat-icon>
      <span>{{ wizardStore.error() }}</span>
    </div>
  }

  <p class="m-0 mb-4 whitespace-pre-line text-sm leading-relaxed text-gray-500">
    <pr-content-view [content]="step().description" />
  </p>

  <div class="flex flex-wrap gap-4 text-[0.8125rem]">
    @if (step().assignedUser && !step().completedAt) {
      <div class="flex items-center gap-1 text-gray-500">
        <mat-icon class="text-gray-400">person</mat-icon>
        <span class="font-medium">Assigned to:</span>
        <span>{{ step().assignedUser?.name }}</span>
      </div>
    }

    @if (step().dueDate && !step().completedAt) {
      <div
        class="flex items-center gap-1"
        [class]="stepStore.isOverdue() ? 'text-warn-500' : 'text-gray-500'"
      >
        <mat-icon
          [class]="stepStore.isOverdue() ? 'text-warn-500' : 'text-gray-400'"
          >event</mat-icon
        >
        <span class="font-medium">{{
          step().dueDate | map: stepStore.formatDueDate : stepStore
        }}</span>
        @if (step().dueDate; as dueDate) {
          <span class="italic">
            ({{ dueDate | moment | amDateFormat: dateFormat }})
          </span>
        }
      </div>
    }

    @if (step().completedAt) {
      <div class="text-success-500 flex items-center gap-1">
        <mat-icon class="text-success-500">check_circle</mat-icon>
        <span class="font-medium">Completed:</span>
        @if (step().completedAt; as completedAt) {
          <span class="italic">
            {{ completedAt | moment | amDateFormat: dateFormat }}
          </span>
        }
        @if (step().completedBy; as completedBy) {
          <span>by {{ completedBy.name }}</span>
        }
      </div>
    }

    @if (step().status === 'validationFailed') {
      <div class="text-warn-500 flex items-center gap-1">
        <mat-icon class="text-warn-500">warning</mat-icon>
        <span class="font-medium">Validation failed - action required</span>
      </div>
    }

    @if (step().validationPassed && stepStore.isComplete()) {
      <div class="text-success-500 flex items-center gap-1">
        <mat-icon class="text-success-500">verified</mat-icon>
        <span class="font-medium">Validation passed</span>
      </div>
    }

    @if (hasUnmetDependencies()) {
      <div
        class="bg-warn-50 text-warn-500 flex w-full flex-col items-start gap-1 rounded p-2"
      >
        <mat-icon class="text-warn-500">link_off</mat-icon>
        <span class="font-medium">Unmet dependencies:</span>
        <div class="mt-2 flex w-full flex-col gap-2">
          @for (dep of unmetDependencies(); track dep.id) {
            <div
              class="border-warn-200 flex items-center gap-2 rounded border bg-white px-2 py-1"
            >
              <mat-icon class="text-warn-500">arrow_right</mat-icon>
              <span class="flex-1 font-medium text-gray-800">{{
                dep.title
              }}</span>
              <pr-practice-setup-step-status
                [dense]="true"
                [status]="dep.status"
              />
            </div>
          }
        </div>
      </div>
    }
  </div>
</div>
